
import { useState, useEffect } from 'react';
import { Plus, Minus, Upload, X, Tag } from 'lucide-react';
import { Game, FAQ } from '../../../../types';
import Modal from '../../../../components/base/Modal';
import Button from '../../../../components/base/Button';
import Input from '../../../../components/base/Input';

interface GameFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (game: Partial<Game>) => void;
  game?: Game;
}

export default function GameFormModal({ isOpen, onClose, onSave, game }: GameFormModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    summary: '',
    coverImage: '',
    status: 'draft' as 'draft' | 'published',
    rulesUrl: '',
    tags: [] as string[], // タグフィールドを追加
    faq: [] as FAQ[],
    vrcGuide: { text: '', url: '', images: [] as string[] },
    instanceGuide: { text: '', images: [] as string[] },
    sources: [{ id: '', type: 'url' as 'url' | 'pdf', name: '', ref: '', page: '' }],
    metadata: {
      targetAge: '',
      playerCount: '',
      playTime: '',
      version: ''
    }
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [imagePreview, setImagePreview] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [newTag, setNewTag] = useState(''); // 新しいタグ入力用
  
  // 定義済みタグ
  const predefinedTags = ['TRPG', 'ボードゲーム', 'カードゲーム', 'パーティーゲーム'];
  
  useEffect(() => {
    if (game) {
      setFormData({
        name: game.name,
        summary: game.summary,
        coverImage: game.coverImage,
        status: game.status,
        rulesUrl: game.rules || '', // 既存のrulesをrulesUrlとして扱う
        tags: game.tags || [], // タグを設定
        faq: game.faq,
        vrcGuide: game.vrcGuide,
        instanceGuide: game.instanceGuide,
        sources: game.sources,
        metadata: game.metadata
      });
      setImagePreview(game.coverImage);
    } else {
      setFormData({
        name: '',
        summary: '',
        coverImage: '',
        status: 'draft',
        rulesUrl: '',
        tags: [],
        faq: [],
        vrcGuide: { text: '', url: '', images: [] },
        instanceGuide: { text: '', images: [] },
        sources: [{ id: '', type: 'url', name: '', ref: '', page: '' }],
        metadata: {
          targetAge: '',
          playerCount: '',
          playTime: '',
          version: ''
        }
      });
      setImagePreview('');
    }
    setErrors({});
    setNewTag('');
  }, [game, isOpen]);
  
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'ゲーム名は必須です';
    }
    
    if (!formData.rulesUrl.trim()) {
      newErrors.rulesUrl = 'ルール参照URLは必須です';
    } else {
      try {
        new URL(formData.rulesUrl);
      } catch {
        newErrors.rulesUrl = '有効なURLを入力してください';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // ファイルサイズチェック（5MB以下）
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, coverImage: 'ファイルサイズは5MB以下にしてください' }));
      return;
    }
    
    // ファイル形式チェック
    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, coverImage: '画像ファイルを選択してください' }));
      return;
    }
    
    setIsUploading(true);
    setErrors(prev => ({ ...prev, coverImage: '' }));
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setFormData(prev => ({ ...prev, coverImage: result }));
      setImagePreview(result);
      setIsUploading(false);
    };
    reader.readAsDataURL(file);
  };
  
  const removeImage = () => {
    setFormData(prev => ({ ...prev, coverImage: '' }));
    setImagePreview('');
  };

  // タグ関連の関数
  const addTag = (tag: string) => {
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
    }
    setNewTag('');
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleAddNewTag = () => {
    if (newTag.trim()) {
      addTag(newTag.trim());
    }
  };
  
  const handleSubmit = (status: 'draft' | 'published') => {
    const dataToSave = { 
      ...formData, 
      status,
      rules: formData.rulesUrl // rulesUrlをrulesとして保存
    };
    
    if (status === 'published' && !validateForm()) {
      return;
    }
    
    onSave(dataToSave);
  };
  
  const addFAQ = () => {
    setFormData(prev => ({
      ...prev,
      faq: [...prev.faq, { id: Date.now().toString(), question: '', answer: '' }]
    }));
  };
  
  const removeFAQ = (index: number) => {
    setFormData(prev => ({
      ...prev,
      faq: prev.faq.filter((_, i) => i !== index)
    }));
  };
  
  const updateFAQ = (index: number, field: 'question' | 'answer', value: string) => {
    setFormData(prev => ({
      ...prev,
      faq: prev.faq.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={game ? 'ゲーム編集' : '新規ゲーム追加'}
      size="xl"
    >
      <div className="space-y-6 max-h-96 overflow-y-auto">
        {/* 基本情報 */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">基本情報</h3>
          
          <Input
            label="ゲーム名 *"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            error={errors.name}
            placeholder="例：りゅうたま"
          />
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              概要
            </label>
            <textarea
              value={formData.summary}
              onChange={(e) => setFormData(prev => ({ ...prev, summary: e.target.value }))}
              placeholder="ゲームの簡単な説明（80-120文字推奨）"
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
            />
          </div>

          {/* タグ設定 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              タグ
            </label>
            
            {/* 選択済みタグ表示 */}
            {formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {formData.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full"
                  >
                    <Tag className="h-3 w-3" />
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="ml-1 text-blue-600 hover:text-blue-800"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* 定義済みタグ選択 */}
            <div className="space-y-2">
              <p className="text-xs text-gray-500">定義済みタグから選択:</p>
              <div className="flex flex-wrap gap-2">
                {predefinedTags.map((tag) => (
                  <Button
                    key={tag}
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => addTag(tag)}
                    disabled={formData.tags.includes(tag)}
                    className={`text-xs ${
                      formData.tags.includes(tag) 
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                        : 'hover:bg-blue-50 hover:text-blue-600'
                    }`}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    {tag}
                  </Button>
                ))}
              </div>
            </div>

            {/* カスタムタグ追加 */}
            <div className="flex gap-2 mt-3">
              <Input
                placeholder="カスタムタグを入力"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddNewTag();
                  }
                }}
                className="flex-1"
              />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleAddNewTag}
                disabled={!newTag.trim() || formData.tags.includes(newTag.trim())}
                className="whitespace-nowrap"
              >
                <Plus className="h-4 w-4 mr-1" />
                追加
              </Button>
            </div>
          </div>
          
          {/* サムネイル画像アップロード */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              サムネイル画像
            </label>
            
            {imagePreview ? (
              <div className="relative inline-block">
                <img
                  src={imagePreview}
                  alt="プレビュー"
                  className="w-32 h-20 object-cover object-top rounded-lg border border-gray-300"
                />
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="image-upload"
                  disabled={isUploading}
                />
                <label
                  htmlFor="image-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <Upload className="h-8 w-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">
                    {isUploading ? 'アップロード中...' : 'クリックして画像を選択'}
                  </span>
                  <span className="text-xs text-gray-500 mt-1">
                    PNG, JPG, GIF (最大5MB)
                  </span>
                </label>
              </div>
            )}
            
            {errors.coverImage && (
              <p className="mt-1 text-sm text-red-600">{errors.coverImage}</p>
            )}
          </div>
        </div>
        
        {/* ルール参照URL */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">ルール情報</h3>
          <Input
            label="ルール参照URL *"
            value={formData.rulesUrl}
            onChange={(e) => setFormData(prev => ({ ...prev, rulesUrl: e.target.value }))}
            error={errors.rulesUrl}
            placeholder="https://atwiki.jp/example または https://notion.so/example"
          />
          <p className="text-xs text-gray-500">
            アットウィキ、Notion、その他のWebページのURLを入力してください
          </p>
        </div>
        
        {/* よくある質問 */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">よくある質問</h3>
            <Button variant="ghost" size="sm" onClick={addFAQ}>
              <Plus className="h-4 w-4 mr-1" />
              質問を追加
            </Button>
          </div>
          
          {formData.faq.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p className="text-sm">まだ質問が登録されていません</p>
              <p className="text-xs mt-1">「質問を追加」ボタンから追加してください</p>
            </div>
          ) : (
            formData.faq.map((faq, index) => (
              <div key={faq.id || index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <span className="text-sm font-medium text-gray-700">質問 {index + 1}</span>
                  <Button variant="ghost" size="sm" onClick={() => removeFAQ(index)}>
                    <Minus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="space-y-3">
                  <Input
                    placeholder="質問内容を入力してください"
                    value={faq.question}
                    onChange={(e) => updateFAQ(index, 'question', e.target.value)}
                  />
                  <textarea
                    placeholder="回答内容を入力してください"
                    value={faq.answer}
                    onChange={(e) => updateFAQ(index, 'answer', e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={3}
                  />
                </div>
              </div>
            ))
          )}
        </div>
        
        {/* メタデータ */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">ゲーム詳細</h3>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="対象年齢"
              value={formData.metadata.targetAge}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                metadata: { ...prev.metadata, targetAge: e.target.value }
              }))}
              placeholder="例：10歳以上"
            />
            <Input
              label="プレイ人数"
              value={formData.metadata.playerCount}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                metadata: { ...prev.metadata, playerCount: e.target.value }
              }))}
              placeholder="例：2-4人"
            />
            <Input
              label="プレイ時間"
              value={formData.metadata.playTime}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                metadata: { ...prev.metadata, playTime: e.target.value }
              }))}
              placeholder="例：30-60分"
            />
            <Input
              label="バージョン"
              value={formData.metadata.version}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                metadata: { ...prev.metadata, version: e.target.value }
              }))}
              placeholder="例：v1.0"
            />
          </div>
        </div>
      </div>
      
      {/* アクションボタン */}
      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
        <Button variant="ghost" onClick={onClose}>
          キャンセル
        </Button>
        <Button variant="secondary" onClick={() => handleSubmit('draft')}>
          下書き保存
        </Button>
        <Button onClick={() => handleSubmit('published')}>
          公開して保存
        </Button>
      </div>
    </Modal>
  );
}